import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { livestreamService } from '../../services/livestream';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import Alert from '../../components/Common/Alert';
import { ImageUtils } from '../../utils/imageUtils';


// Utility functions moved outside to avoid hoisting issues and redundant re-renders
const calculateAnalyticsData = (livestreamData, biddingsData) => {
  const totalBiddings = biddingsData.length;
  const successfulBiddings = biddingsData.filter(b => b.status === 'ended' && b.winner).length;
  const totalRevenue = biddingsData
    .filter(b => b.winner && b.current_highest_bid)
    .reduce((sum, b) => sum + parseFloat(b.current_highest_bid || 0), 0);

  const averageBidAmount = successfulBiddings > 0 ? totalRevenue / successfulBiddings : 0;

  // Calculate duration in minutes
  let duration = 0;
  if (livestreamData.actual_start_time && livestreamData.end_time) {
    const start = new Date(livestreamData.actual_start_time);
    const end = new Date(livestreamData.end_time);
    duration = Math.round((end - start) / (1000 * 60)); // in minutes
  }

  return {
    totalViewers: livestreamData.total_viewers || livestreamData.viewer_count || 0,
    peakViewers: livestreamData.peak_viewers || livestreamData.viewer_count || 0,
    totalBiddings,
    successfulBiddings,
    totalRevenue,
    creditsConsumed: livestreamData.credits_consumed || 0,
    duration,
    chatMessages: livestreamData.total_messages || 0,
    productsSold: successfulBiddings,
    averageBidAmount,
    viewerRetention: 75, // Mock data - you can calculate real retention
    engagementRate: totalBiddings > 0 ? (successfulBiddings / totalBiddings) * 100 : 0
  };
};

const generateTimelineRecords = (livestreamData, biddingsData) => {
  const timeline = [];

  if (livestreamData.actual_start_time) {
    timeline.push({
      time: new Date(livestreamData.actual_start_time),
      event: 'Livestream Started',
      type: 'start',
      description: 'Livestream went live'
    });
  }

  // Add bidding events
  biddingsData.forEach(bidding => {
    if (bidding.started_at) {
      timeline.push({
        time: new Date(bidding.started_at),
        event: 'Bidding Started',
        type: 'bidding',
        description: `Bidding started for ${bidding.product?.name || 'product'}`
      });
    }

    if (bidding.ended_at) {
      timeline.push({
        time: new Date(bidding.ended_at),
        event: 'Bidding Ended',
        type: 'bidding',
        description: `Bidding ended for ${bidding.product?.name || 'product'}${bidding.winner ? ` - Won by ${bidding.winner_name}` : ''}`
      });
    }
  });

  if (livestreamData.end_time) {
    timeline.push({
      time: new Date(livestreamData.end_time),
      event: 'Livestream Ended',
      type: 'end',
      description: 'Livestream concluded'
    });
  }

  return timeline.sort((a, b) => a.time - b.time);
};

const formatCurrencyValue = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR'
  }).format(amount);
};

const formatDurationValue = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

const formatDateTimeValue = (dateString) => {
  return new Date(dateString).toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const LivestreamAnalytics = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [livestream, setLivestream] = useState(null);
  const [analytics, setAnalytics] = useState({
    totalViewers: 0,
    peakViewers: 0,
    totalBiddings: 0,
    successfulBiddings: 0,
    totalRevenue: 0,
    creditsConsumed: 0,
    duration: 0,
    chatMessages: 0,
    productsSold: 0,
    averageBidAmount: 0,
    viewerRetention: 0,
    engagementRate: 0
  });
  const [biddings, setBiddings] = useState([]);
  const [timelineData, setTimelineData] = useState([]);

  const loadAnalyticsData = useCallback(async () => {
    try {
      setLoading(true);

      // Load livestream basic data
      const livestreamData = await livestreamService.getLivestream(id);
      setLivestream(livestreamData);

      // Load biddings data for this livestream
      const biddingsResponse = await livestreamService.getBiddings({ livestream: id });
      const biddingsData = biddingsResponse.results || biddingsResponse || [];
      setBiddings(biddingsData);

      // Calculate analytics
      const calculatedAnalytics = calculateAnalyticsData(livestreamData, biddingsData);
      setAnalytics(calculatedAnalytics);

      // Generate mock timeline data (you can replace with real data)
      const timeline = generateTimelineRecords(livestreamData, biddingsData);
      setTimelineData(timeline);

    } catch (error) {
      console.error('Error loading analytics data:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to load analytics data. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);


  if (loading) {
    return <LoadingSpinner text="Loading analytics..." />;
  }

  if (!livestream) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger">
          Livestream not found.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-950 p-3 md:p-4 lg:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Enhanced Header with Gradient */}
        <div className="relative mb-6 overflow-hidden rounded-2xl bg-gradient-to-br from-dark-800/60 via-dark-800/40 to-transparent backdrop-blur-xl border border-white/5 p-5 shadow-2xl">
          {/* Animated gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-red-500/5 via-transparent to-blue-500/5 opacity-50"></div>

          <div className="relative flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="absolute inset-0 bg-red-500/20 rounded-xl blur-lg"></div>
                <div className="relative w-12 h-12 bg-gradient-to-br from-red-500/20 to-red-600/10 rounded-xl flex items-center justify-center border border-red-500/30 shadow-lg shadow-red-500/10">
                  <i className="bi bi-graph-up-arrow text-red-500 text-xl"></i>
                </div>
              </div>
              <div>
                <h1 className="text-2xl font-black text-white tracking-tight uppercase mb-0.5 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">Session Intel</h1>
                <p className="text-gray-500 text-[10px] font-bold uppercase tracking-[0.2em]">Broadcast Performance Analysis</p>
              </div>
            </div>
            <div className="flex gap-2.5">
              <button
                onClick={() => navigate(`/livestreams/${id}/details`)}
                className="group px-4 py-2 bg-dark-900/50 border border-white/10 text-gray-400 rounded-xl hover:text-white hover:border-white/20 hover:bg-dark-900/70 transition-all duration-300 font-bold text-xs uppercase tracking-widest shadow-lg"
              >
                <span className="flex items-center gap-2">
                  <i className="bi bi-file-earmark-text group-hover:scale-110 transition-transform"></i>
                  Full Profile
                </span>
              </button>
              <button
                onClick={() => navigate('/livestreams')}
                className="group px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-300 font-bold text-xs uppercase tracking-widest shadow-lg shadow-red-500/30 hover:shadow-red-500/50 hover:scale-105"
              >
                <span className="flex items-center gap-2">
                  <i className="bi bi-box-arrow-left group-hover:scale-110 transition-transform"></i>
                  Exit Portal
                </span>
              </button>
            </div>
          </div>
        </div>

        {alert && (
          <Alert
            type={alert.type}
            message={alert.message}
            onClose={() => setAlert(null)}
            className="mb-4"
          />
        )}

        {/* Session Metadata Card */}
        <div className="bg-dark-800/40 backdrop-blur-xl rounded-2xl border border-transparent p-5 mb-6 shadow-xl">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="w-24 h-24 rounded-xl overflow-hidden border border-transparent shadow-inner shrink-0">
              {livestream.thumbnail ? (
                <img
                  src={ImageUtils.getFullImageUrl(livestream.thumbnail)}
                  alt={livestream.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-dark-950 flex items-center justify-center">
                  <i className="bi bi-broadcast text-gray-800 text-2xl"></i>
                </div>
              )}
            </div>
            <div className="flex-grow text-center md:text-left">
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-2">
                <span className="px-2.5 py-0.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-[10px] font-bold uppercase tracking-widest">
                  SESSION {livestream.status}
                </span>
                <span className="text-gray-500 text-[10px] font-bold uppercase tracking-widest">
                  <i className="bi bi-clock-history mr-1"></i>
                  {formatDateTimeValue(livestream.actual_start_time || livestream.created_at)}
                </span>
              </div>
              <h2 className="text-xl font-bold text-white mb-1.5 uppercase tracking-tight truncate">{livestream.title}</h2>
              <p className="text-gray-500 text-xs font-medium max-w-2xl line-clamp-1">{livestream.description || 'Secure broadcast event metadata.'}</p>
            </div>
            <div className="grid grid-cols-2 gap-6 shrink-0 border-l border-transparent pl-6 hidden lg:grid">
              <div>
                <div className="text-gray-600 text-[10px] font-bold uppercase tracking-widest mb-1">Engage Duration</div>
                <div className="text-white text-lg font-bold">{formatDurationValue(analytics.duration)}</div>
              </div>
              <div>
                <div className="text-gray-600 text-[10px] font-bold uppercase tracking-widest mb-1">Node Cost</div>
                <div className="text-white text-lg font-bold">{analytics.creditsConsumed} CRP</div>
              </div>
            </div>
          </div>
        </div>

        {/* Metric Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Network Reach', value: analytics.totalViewers.toLocaleString(), sub: `PEAK: ${analytics.peakViewers}`, icon: 'bi-eye-fill', color: 'text-blue-500', bg: 'bg-blue-500/10' },
            { label: 'Gross Yield', value: formatCurrencyValue(analytics.totalRevenue).split('.')[0], sub: `AVG: ₹${Math.round(analytics.averageBidAmount)}`, icon: 'bi-lightning-charge-fill', color: 'text-red-500', bg: 'bg-red-500/10' },
            { label: 'Total Conflicts', value: analytics.totalBiddings, sub: `${analytics.successfulBiddings} CONCLUDED`, icon: 'bi-hammer', color: 'text-red-500', bg: 'bg-red-500/10' },
            { label: 'Units Deployed', value: analytics.productsSold, sub: `${analytics.engagementRate.toFixed(1)}% CONV`, icon: 'bi-box-seam-fill', color: 'text-green-500', bg: 'bg-green-500/10' }
          ].map((stat, i) => (
            <div key={i} className="bg-dark-800/40 backdrop-blur-xl rounded-2xl border border-transparent p-4 group hover:bg-dark-800/60 transition-all shadow-lg">
              <div className={`mb-3 w-9 h-9 ${stat.bg} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform border border-transparent`}>
                <i className={`bi ${stat.icon} ${stat.color} text-base`}></i>
              </div>
              <div className="text-xl font-bold text-white mb-0.5 tracking-tighter">{stat.value}</div>
              <div className="text-gray-500 text-[10px] font-bold uppercase tracking-[0.12em] mb-1.5">{stat.label}</div>
              <div className="text-[9px] font-bold text-gray-700 uppercase tracking-widest">{stat.sub}</div>
            </div>
          ))}
        </div>

        {/* Performance Metrics & Timeline */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Performance Metrics Card */}
          <div className="bg-dark-800/40 backdrop-blur-xl rounded-2xl border border-transparent overflow-hidden shadow-2xl">
            <div className="px-5 py-4 border-b border-white/5 bg-gradient-to-r from-red-500/5 to-transparent">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-red-500/10 rounded-lg flex items-center justify-center border border-red-500/20">
                  <i className="bi bi-graph-up text-red-500 text-sm"></i>
                </div>
                <div>
                  <h4 className="text-xs font-bold text-white tracking-widest uppercase mb-0">Performance Metrics</h4>
                  <p className="text-gray-600 text-[10px] font-bold uppercase tracking-[0.15em] mb-0">Engagement Analytics</p>
                </div>
              </div>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 gap-5">
                {/* Viewer Retention */}
                <div className="group">
                  <div className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-3">Viewer Retention</div>
                  <div className="flex items-center gap-3">
                    <div className="flex-grow h-2 bg-dark-900/50 rounded-full overflow-hidden border border-white/5">
                      <div
                        className="h-full bg-gradient-to-r from-green-500 to-emerald-400 rounded-full transition-all duration-1000 ease-out shadow-lg shadow-green-500/20"
                        style={{ width: `${analytics.viewerRetention}%` }}
                      ></div>
                    </div>
                    <span className="text-green-500 font-bold text-sm min-w-[45px] text-right">{analytics.viewerRetention}%</span>
                  </div>
                </div>

                {/* Engagement Rate */}
                <div className="group">
                  <div className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-3">Engagement Rate</div>
                  <div className="flex items-center gap-3">
                    <div className="flex-grow h-2 bg-dark-900/50 rounded-full overflow-hidden border border-white/5">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full transition-all duration-1000 ease-out shadow-lg shadow-blue-500/20"
                        style={{ width: `${analytics.engagementRate}%` }}
                      ></div>
                    </div>
                    <span className="text-blue-500 font-bold text-sm min-w-[45px] text-right">{analytics.engagementRate.toFixed(1)}%</span>
                  </div>
                </div>

                {/* Revenue per Hour */}
                <div className="bg-dark-900/30 rounded-xl p-4 border border-white/5 hover:border-green-500/20 transition-all group">
                  <div className="text-gray-600 text-xs font-bold uppercase tracking-widest mb-2">Revenue/Hour</div>
                  <div className="text-green-500 font-bold text-lg tracking-tight">
                    {analytics.duration > 0 ? formatCurrencyValue((analytics.totalRevenue / analytics.duration) * 60) : formatCurrencyValue(0)}
                  </div>
                  <div className="text-gray-700 text-xs font-bold uppercase tracking-widest mt-1">HOURLY YIELD</div>
                </div>

                {/* Credits per Hour */}
                <div className="bg-dark-900/30 rounded-xl p-4 border border-white/5 hover:border-yellow-500/20 transition-all group">
                  <div className="text-gray-600 text-xs font-bold uppercase tracking-widest mb-2">Credits/Hour</div>
                  <div className="text-yellow-500 font-bold text-lg tracking-tight">
                    {analytics.duration > 0 ? ((analytics.creditsConsumed / analytics.duration) * 60).toFixed(1) : '0'}
                  </div>
                  <div className="text-gray-700 text-xs font-bold uppercase tracking-widest mt-1">HOURLY BURN</div>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline Card */}
          <div className="bg-dark-800/40 backdrop-blur-xl rounded-2xl border border-transparent overflow-hidden shadow-2xl">
            <div className="px-6 py-5 border-b border-white/5 bg-gradient-to-r from-blue-500/5 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center border border-blue-500/20">
                  <i className="bi bi-clock-history text-blue-500 text-lg"></i>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-white tracking-widest uppercase mb-0">Event Timeline</h4>
                  <p className="text-gray-600 text-xs font-bold uppercase tracking-[0.2em] mb-0">Session Chronology</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4 max-h-[320px] overflow-y-auto custom-scrollbar pr-2">
                {timelineData.map((item, index) => (
                  <div key={index} className="flex gap-4 group">
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${item.type === 'start'
                          ? 'bg-green-500/10 border-green-500/30 text-green-500 group-hover:bg-green-500/20'
                          : item.type === 'end'
                            ? 'bg-gray-500/10 border-gray-500/30 text-gray-500 group-hover:bg-gray-500/20'
                            : 'bg-red-500/10 border-red-500/30 text-red-500 group-hover:bg-red-500/20'
                        }`}>
                        <i className={`bi ${item.type === 'start' ? 'bi-play-fill' :
                            item.type === 'end' ? 'bi-stop-fill' : 'bi-hammer'
                          } text-xs`}></i>
                      </div>
                      {index < timelineData.length - 1 && (
                        <div className="w-0.5 h-full bg-white/5 mt-2"></div>
                      )}
                    </div>
                    <div className="flex-grow pb-4">
                      <div className="text-white font-bold text-sm uppercase tracking-tight mb-1">{item.event}</div>
                      <div className="text-gray-500 text-xs mb-1.5 leading-relaxed">{item.description}</div>
                      <div className="flex items-center gap-2 text-gray-700 text-xs font-bold uppercase tracking-widest">
                        <i className="bi bi-clock text-[10px]"></i>
                        {item.time.toLocaleTimeString('en-IN', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Results Table */}
        {biddings.length > 0 && (
          <div className="bg-dark-800/40 backdrop-blur-xl rounded-2xl border border-transparent overflow-hidden shadow-2xl mb-8">
            <div className="px-6 py-5 border-b border-transparent">
              <h4 className="text-sm font-bold text-white tracking-widest uppercase mb-1">Concluded Conflicts</h4>
              <p className="text-gray-600 text-xs font-bold uppercase tracking-[0.2em]">Bidding Authorization Records</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-dark-900/50 text-gray-500 text-xs font-bold uppercase tracking-[0.2em] border-b border-transparent">
                  <tr>
                    <th className="px-6 py-4">Descriptor</th>
                    <th className="px-6 py-4">Valuation</th>
                    <th className="px-6 py-4">Target Node</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4 text-right">Cycle</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {biddings.map((bidding) => (
                    <tr key={bidding.id} className="group hover:bg-white/[0.02] transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg overflow-hidden border border-transparent">
                            {bidding.product?.primary_image && (
                              <img
                                src={ImageUtils.getProductImageUrl(bidding.product)}
                                alt={bidding.product.name}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                              />
                            )}
                          </div>
                          <div>
                            <div className="text-xs font-bold text-white uppercase tracking-tight">{bidding.product?.name || 'Unknown Unit'}</div>
                            <div className="text-xs text-gray-600 uppercase tracking-widest font-bold">{bidding.product?.category_name || 'GENERIC'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-xs text-gray-600 uppercase font-bold mb-1">FINAL BID</div>
                        <div className="text-xs font-bold text-red-500 tracking-tighter">
                          {bidding.current_highest_bid ? formatCurrencyValue(bidding.current_highest_bid) : 'NULL'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {bidding.winner_name ? (
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full bg-green-500/10 flex items-center justify-center border border-green-500/20">
                              <i className="bi bi-person-fill text-green-500 text-xs"></i>
                            </div>
                            <span className="text-xs font-bold text-white uppercase tracking-tight">{bidding.winner_name}</span>
                          </div>
                        ) : (
                          <span className="text-xs font-bold text-gray-700 uppercase">NO ACQUISITION</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-widest border ${bidding.status === 'ended' ? 'bg-white/5 border-transparent text-gray-500' : 'bg-green-500/10 border-green-500/20 text-green-500'
                          }`}>
                          {bidding.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-xs font-bold text-gray-600">
                          {bidding.started_at && bidding.ended_at ?
                            `${Math.round((new Date(bidding.ended_at) - new Date(bidding.started_at)) / 1000)}s` :
                            `${bidding.timer_duration || 0}s`}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


export default LivestreamAnalytics;





